import React from 'react';
import { GRID_COLUMN_GAP_XLARGE } from '@noths/polaris-client-ribbons-base';

import * as styles from './Carousel.styles';
import { IconButton } from 'client-ribbons-design-system/components/elements/Button/IconButton/IconButton';
import { IconArrowBack } from 'client-ribbons-design-system/components/foundations/Iconography/IconArrowBack';
import { IconArrowForward } from 'client-ribbons-design-system/components/foundations/Iconography/IconArrowForward';
import type { CarouselNavigateEvent } from 'client-ribbons-design-system/components/internal/Carousel/useCarousel';
import {
  smoothScrollCarousel,
  useCarousel,
  useCarouselControls,
} from 'client-ribbons-design-system/components/internal/Carousel/useCarousel';

export interface CarouselSubheadingLink {
  href: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  text: string;
}

export interface CarouselProps {
  children: React.ReactNode;
  columnGap?: number;
  forceNextItemControlEnabled?: boolean;
  /*
    TODO remove these props when possible as they only used for mocking behaviour in tests
    used in  webapps/browse-productdetails/src/components/organisms/RecommendedProductsShelf/containers/RecommendedProductsShelfContainer.tsx
   */
  forcePreviousItemControlEnabled?: boolean;
  onNavigateLeft?: (details: CarouselNavigateEvent) => void;
  onNavigateRight?: (details: CarouselNavigateEvent) => void;
  subheadingLink?: CarouselSubheadingLink;
  title?: string;
  titleSize?: 'regular'; // TODO: remove
}

export const Carousel = ({
  children,
  columnGap = GRID_COLUMN_GAP_XLARGE,
  forceNextItemControlEnabled,
  forcePreviousItemControlEnabled,
  onNavigateLeft,
  onNavigateRight,
  subheadingLink,
  title,
  titleSize = 'regular',
}: CarouselProps) => {
  const itemCount = React.Children.count(children);
  const { carouselRef, isSwipeNavigation, showNextControl, showPreviousControl } = useCarousel({
    itemCount,
    onNavigateLeft,
    onNavigateRight,
    columnGap,
    forcePreviousItemControlEnabled,
    forceNextItemControlEnabled,
  });

  const { onNext, onPrevious } = useCarouselControls({
    isSwipeNavigation,
    carouselRef,
    itemCount,
    smoothScrollCarouselDI: smoothScrollCarousel,
    onNavigateLeft,
    onNavigateRight,
    columnGap,
  });

  const displayControls = showPreviousControl || showNextControl;

  return (
    <section aria-labelledby="carouselheading" aria-roledescription="carousel">
      <header css={[styles.header, !!subheadingLink && styles.headerWithSubheading]}>
        {title && !subheadingLink && (
          <h2
            css={[styles.title, titleSize === 'regular' ? styles.titleRegular : styles.titleSmall]}
            id="carouselheading"
          >
            {title}
          </h2>
        )}
        {title && subheadingLink && (
          <div css={styles.headingGroup}>
            <h2
              css={[
                styles.titleStacked,
                titleSize === 'regular' ? styles.titleRegular : styles.titleSmall,
              ]}
              id="carouselheading"
            >
              {title}
            </h2>
            <a
              css={styles.subheadingLink}
              href={subheadingLink.href}
              onClick={subheadingLink.onClick}
            >
              {subheadingLink.text}
            </a>
          </div>
        )}
        {displayControls && (
          <fieldset aria-controls="carousel" aria-label="carousel buttons" css={styles.controls}>
            <IconButton
              Icon={IconArrowBack}
              aria-label="Scroll to previous item"
              brandColorMode
              disabled={!showPreviousControl}
              onClick={onPrevious}
              size="small"
            />
            <IconButton
              Icon={IconArrowForward}
              aria-label="Scroll to next item"
              brandColorMode
              disabled={!showNextControl}
              onClick={onNext}
              size="small"
            />
          </fieldset>
        )}
      </header>
      <div>
        <div css={styles.carouselContent} data-testid="carousel-content" ref={carouselRef}>
          <ul
            css={styles.carouselWrapper}
            style={
              {
                '--columnGap': `${columnGap}px`,
              } as styles.CarouselCss
            }
          >
            {children}
          </ul>
        </div>
      </div>
    </section>
  );
};
